import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { AxiosError } from 'axios'
import { useAuth } from '../hooks/useAuth'
import { useAuthStore } from '../stores/authStore'

// Helper to extract error message from AxiosError
function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError && err.response?.data) {
    const data = err.response.data
    if (Array.isArray(data.detail)) {
      return data.detail[0]?.msg || fallback
    }
    if (typeof data.detail === 'string') {
      return data.detail
    }
  }
  if (err instanceof Error) {
    return err.message
  }
  return fallback
}

// Telegram bot username from env
const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'JanusTherapyBot'

// Google Client ID from env
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

// App version from env
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.1.0'

// Telegram Login Widget types
declare global {
  interface Window {
    TelegramLoginCallback?: (user: TelegramUser) => void
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void
          renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void
          prompt: () => void
        }
      }
    }
  }
}

interface GoogleButtonConfig {
  type: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
  locale?: string
}

interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

interface GoogleInitConfig {
  client_id: string
  callback: (response: GoogleCredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
}

interface GoogleCredentialResponse {
  credential: string
  select_by: string
}

export default function LoginPage() {
  const lastUser = useAuthStore((s) => s.lastUser)
  const clearLastUser = useAuthStore((s) => s.clearLastUser)
  const [email, setEmail] = useState(lastUser?.email || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [telegramLoading, setTelegramLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const telegramWidgetRef = useRef<HTMLDivElement>(null)
  const googleButtonRef = useRef<HTMLDivElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const { login, telegramLogin, googleLogin } = useAuth()

  // Handle Telegram auth callback
  const handleTelegramAuth = useCallback(async (user: TelegramUser) => {
    setTelegramLoading(true)
    setError('')
    try {
      await telegramLogin(user)
    } catch (err) {
      setError(getErrorMessage(err, 'Ошибка входа через Telegram'))
    } finally {
      setTelegramLoading(false)
    }
  }, [telegramLogin])

  // Set up Telegram Login Widget (hidden)
  useEffect(() => {
    window.TelegramLoginCallback = handleTelegramAuth

    if (telegramWidgetRef.current) {
      telegramWidgetRef.current.innerHTML = ''

      const script = document.createElement('script')
      script.src = 'https://telegram.org/js/telegram-widget.js?22'
      script.async = true
      script.setAttribute('data-telegram-login', TELEGRAM_BOT_USERNAME)
      script.setAttribute('data-size', 'large')
      script.setAttribute('data-onauth', 'TelegramLoginCallback(user)')
      script.setAttribute('data-request-access', 'write')
      // Widget loads automatically

      telegramWidgetRef.current.appendChild(script)
    }

    return () => {
      delete window.TelegramLoginCallback
    }
  }, [handleTelegramAuth])


  // Handle Google auth callback
  const handleGoogleAuth = useCallback(async (response: GoogleCredentialResponse) => {
    setGoogleLoading(true)
    setError('')
    try {
      await googleLogin(response.credential)
    } catch (err) {
      setError(getErrorMessage(err, 'Ошибка входа через Google'))
    } finally {
      setGoogleLoading(false)
    }
  }, [googleLogin])

  // Initialize Google Sign-In with renderButton
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonRef.current) return

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleAuth,
          auto_select: false,
          cancel_on_tap_outside: true,
        })
        // Render the official Google button
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          logo_alignment: 'left',
          width: 250,
        })
      }
    }
    document.body.appendChild(script)

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [handleGoogleAuth])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(email, password)
    } catch (err) {
      setError(getErrorMessage(err, 'Ошибка входа'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-12 bg-gradient-calm">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-primary-200 rounded-full blur-xl opacity-50" />
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-primary breathe">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-center text-2xl sm:text-3xl font-bold text-neutral-800">
          Janus Therapy
        </h1>
        <p className="text-center text-xs text-neutral-400 font-mono">
          v{APP_VERSION}
        </p>
        <p className="mt-1 sm:mt-2 text-center text-sm sm:text-base text-neutral-500">
          Ваш путь к эмоциональному благополучию
        </p>
      </div>

      <div className="mt-4 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up">
        <div className="card-elevated py-5 px-5 sm:py-8 sm:px-10">
          {/* "Continue as..." for returning email users */}
          {lastUser?.loginMethod === 'email' && lastUser.email && (
            <div className="mb-5 sm:mb-6">
              <button
                type="button"
                data-testid="continue-as"
                onClick={() => {
                  setEmail(lastUser.email!)
                  passwordRef.current?.focus()
                }}
                className="w-full flex items-center gap-3 p-3 sm:p-4 rounded-2xl border-2 border-primary-200 bg-primary-50/50 hover:bg-primary-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  {lastUser.avatarUrl ? (
                    <img src={lastUser.avatarUrl} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <span className="text-primary-600 font-semibold text-lg">
                      {(lastUser.displayName || lastUser.email)?.[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-800 truncate">
                    {lastUser.displayName || lastUser.email}
                  </p>
                  <p className="text-xs text-neutral-500">Введите пароль для входа</p>
                </div>
                <svg className="w-5 h-5 text-primary-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => {
                  clearLastUser()
                  setEmail('')
                }}
                className="mt-2 text-xs text-neutral-400 hover:text-neutral-600 transition-colors w-full text-center"
              >
                Войти другим аккаунтом
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {error && (
              <div className="bg-error-light text-error-dark p-4 rounded-2xl text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                  Пароль
                </label>
                <Link to="/forgot-password" className="text-xs text-primary-600 hover:text-primary-700 transition-colors">
                  Забыли пароль?
                </Link>
              </div>
              <input
                ref={passwordRef}
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className={clsx(
                'btn-primary w-full text-base py-3 sm:py-3.5',
                loading && 'opacity-70 cursor-wait'
              )}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Входим...
                </span>
              ) : (
                'Войти'
              )}
            </button>
          </form>

          <div className="mt-4 sm:mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-neutral-400">или</span>
              </div>
            </div>

            {/* Social login buttons */}
            <div className="mt-3 sm:mt-4 flex flex-col items-center gap-2 sm:gap-3">
              {/* Telegram Widget - native pill shape */}
              <div ref={telegramWidgetRef} data-testid="telegram-login" />

              {/* Telegram loading indicator */}
              {telegramLoading && (
                <div className="flex items-center justify-center gap-2 py-3 text-[#2AABEE]">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Авторизация...
                </div>
              )}

              {/* Google Login Button - rendered by Google SDK */}
              {GOOGLE_CLIENT_ID && (
                <div ref={googleButtonRef} data-testid="google-login" className="flex justify-center">
                  {googleLoading && (
                    <div className="flex items-center justify-center gap-2 py-3 text-neutral-600">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Авторизация...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <p className="mt-4 sm:mt-6 text-center text-sm text-neutral-500">
            Нет аккаунта?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Зарегистрироваться
            </Link>
          </p>

          {/* Security hint - inside card for mobile fit */}
          <p className="mt-3 sm:mt-4 text-center text-xs text-neutral-400">
            Безопасный вход с шифрованием данных
          </p>
        </div>
      </div>
    </div>
  )
}
